import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import {
  Bell,
  FileText,
  Gauge,
  Home,
  LogOut,
  Settings,
  User,
} from 'lucide-react'
import { Logo } from '@/components/brand/Logo'
import { Avatar, AvatarFallback, getInitials } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { getDisplayName, logout } from '@/features/auth/api/auth'
import { useAuthStore } from '@/features/auth/authStore'
import { useActiveContract } from '@/features/auth/hooks/useActiveContract'
import { useMessaggi } from '@/lib/graphql/hooks'
import { cn } from '@/lib/utils'
import { ContractSwitcher } from './ContractSwitcher'

const navItems = [
  { to: '/', label: 'Home', icon: Home, end: true },
  { to: '/bollette', label: 'Bollette', icon: FileText },
  { to: '/consumi', label: 'Consumi', icon: Gauge },
  { to: '/profilo', label: 'Profilo', icon: User },
]

export function AppShell() {
  const location = useLocation()
  return (
    <div className="flex h-full flex-col bg-background">
      <Header />

      <main className="flex-1 overflow-y-auto pb-20 md:pb-0">
        <div className="container py-6 md:py-8">
          <ErrorBoundary resetKey={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>

      <BottomNav />
    </div>
  )
}

function Header() {
  return (
    <header className="sticky top-0 z-40 border-b border-border bg-card/95 backdrop-blur">
      <div className="container flex h-16 items-center justify-between gap-4">
        <Logo size="sm" />

        <nav className="hidden md:flex md:items-center md:gap-0.5">
          {navItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                )
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <ContractSwitcher />
          <NotificheBell />
          <UserMenu />
        </div>
      </div>
    </header>
  )
}

function BottomNav() {
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-card/95 backdrop-blur md:hidden">
      <div className="container grid grid-cols-4">
        {navItems.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              cn(
                'flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition-colors',
                isActive ? 'text-secondary' : 'text-muted-foreground',
              )
            }
          >
            <Icon className="h-5 w-5" />
            {label}
          </NavLink>
        ))}
      </div>
    </nav>
  )
}

function IconActionButton({
  children,
  badge,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { badge?: number }) {
  return (
    <button
      type="button"
      className="relative inline-flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      {...props}
    >
      {children}
      {badge !== undefined && badge > 0 && (
        <span className="absolute right-1.5 top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold leading-none text-destructive-foreground">
          {badge > 9 ? '9+' : badge}
        </span>
      )}
    </button>
  )
}

/**
 * Bottone campanella header: porta a /notifiche e mostra un badge con
 * il conteggio messaggi non letti (`ListMessaggi`). Gli alert del contratto
 * non concorrono al badge — sono già visibili in pagina come banner.
 */
function NotificheBell() {
  const navigate = useNavigate()
  const cliente = useActiveContract().cliente
  const { data: messaggi } = useMessaggi(cliente?.codice_cliente)
  const unread = (messaggi ?? []).filter((m) => !m.letto).length

  return (
    <IconActionButton
      aria-label={unread > 0 ? `Notifiche, ${unread} non lette` : 'Notifiche'}
      badge={unread}
      onClick={() => navigate('/notifiche')}
    >
      <Bell className="h-5 w-5" />
    </IconActionButton>
  )
}

function UserMenu() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const reset = useAuthStore((s) => s.reset)

  async function onLogout() {
    await logout()
    reset()
    navigate('/login', { replace: true })
  }

  const displayName = user ? getDisplayName(user) : '—'
  const initials = getInitials(displayName)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="ml-1 inline-flex items-center gap-2 rounded-full p-1 transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Menu utente"
        >
          <Avatar>
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[16rem]">
        <DropdownMenuLabel>
          <div className="flex flex-col gap-0.5">
            <span className="text-[11px] uppercase tracking-wider">Loggato come</span>
            <span className="text-sm font-bold text-foreground">{displayName}</span>
            {user?.email && (
              <span className="truncate text-xs font-normal text-muted-foreground">
                {user.email}
              </span>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/profilo')}>
          <User className="h-4 w-4" />
          Il mio profilo
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate('/profilo/sicurezza')}>
          <Settings className="h-4 w-4" />
          Sicurezza
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem destructive onSelect={onLogout}>
          <LogOut className="h-4 w-4" />
          Esci
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
